import { useState, useEffect, useCallback } from 'react'

/**
 * Tiny localStorage-backed preference: are internal-chat notifications muted?
 * When muted, the sidebar unread badge is hidden. Synced across components
 * (the sidebar badge + the in-chat toggle live in different trees) via a custom
 * window event + the native `storage` event (other tabs).
 */
const KEY = 'chat-muted'
const EVT = 'chat-mute-changed'

function read(): boolean {
  try {
    return localStorage.getItem(KEY) === 'true'
  } catch {
    return false
  }
}

export function useChatMute() {
  const [muted, setMuted] = useState<boolean>(read)

  useEffect(() => {
    const sync = () => setMuted(read())
    window.addEventListener(EVT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(EVT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const toggle = useCallback(() => {
    const next = !read()
    try {
      localStorage.setItem(KEY, String(next))
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event(EVT))
  }, [])

  return { muted, toggle }
}
